import React, { useEffect } from 'react'

import {
  useLocation,
  Route,
  Navigate,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router'
import { RouterProvider } from 'react-router/dom'
import { Spinner } from 'src/components/__legacy__/spinner/spinner'
import { Paths } from 'src/constants/paths'
import { Authenticator } from 'src/features/auth/components'
import { LogoutCompleteScreen } from 'src/features/auth/components/LogoutCompleteScreen/LogoutCompleteScreen'
import {
  ResendDeliveryMail,
  SignAuthScreen,
} from 'src/features/explanation/components'
import { PatientPinSettingScreen } from 'src/features/explanation/components/PatientPinSettingScreen/PatientPinSettingScreen'
import { PatientReservationScreenV2 } from 'src/features/explanation/components/PatientReservationScreenV2/PatientReservationScreenV2'
import { CompleteDocuSignAction } from 'src/features/explanationRoom/components/CompleteDocuSignAction/CompleteDocuSignAction'
import { ExplanationRoomRoutes } from 'src/features/explanationRoom/routes'
import {
  ExternalTransmissionPolicyScreen,
  PrivacyPolicyScreen,
  TermsOfServiceScreen,
} from 'src/features/misc/components'
import { AuthenticatedTermsScreen } from 'src/features/misc/components/AuthenticatedTermsScreen/AuthenticatedTermsScreen'
import { OperationManualScreen } from 'src/features/misc/components/OperationManualScreen/OperationManualScreen'
import { ReleaseNoteScreen } from 'src/features/misc/components/ReleaseNoteScreen/ReleaseNoteScreen'
import { PatientVideoCallExpiredScreen } from 'src/features/patient/components/PatientVideoCallExpiredScreen/PatientVideoCallExpiredScreen'
import { PatientVideoCallForPatientScreen } from 'src/features/patient/components/PatientVideoCallForPatientScreen/PatientVideoCallForPatientScreen'
import { PatientVideoCallScreen } from 'src/features/patient/components/PatientVideoCallScreen/PatientVideoCallScreen'
import { PatientVideoCallSignInScreen } from 'src/features/patient/components/PatientVideoCallSignInScreen/PatientVideoCallSignInScreen'
import { ReferredPatientFileViewerScreen } from 'src/features/referredPatient/components/ReferredPatientFileViewerScreen/ReferredPatientFileViewerScreen'
import { HospitalSharedFileViewer } from 'src/features/trial/components/HospitalSharedFileViewer/HospitalSharedFileViewer'
import { VideoCallConnectionTestScreen } from 'src/features/videoCall/components/VideoCallConnectionTestScreen/VideoCallConnectionTestScreen'
import { HospitalTrialPatientVideoCallScreen } from 'src/features/visit/components/HospitalTrialPatientVideoCallScreen/HospitalTrialPatientVideoCallScreen'
import { PatientTrialPatientVideoCallScreen } from 'src/features/visit/components/TrialPatientVideoCallScreen/PatientTrialPatientVideoCallScreen'
import { WorksheetFileViewerScreen } from 'src/features/worksheet/components/WorksheetFileViewerScreen/WorksheetFileViewerScreen'
import { WorksheetPrintScreen } from 'src/features/worksheet/components/WorksheetPrintScreen/WorksheetPrintScreen'

import { AgreementContainer } from './auth/agreement/agreement-container'
import {
  AuthenticateContainer,
  memberIdParamName,
  membersParamName,
  tokenParamName as AuthenticateTokenParamName,
} from './auth/authenticate/authenticate-container'
import { HttpError403 } from './auth/http-error/http-error-403'
import { LoginContainer } from './auth/login/login-container'
import { OAuthCallback } from './auth/login/oauth-callback/oauth-callback'
import { TemporaryPasswordLoginContainer } from './auth/login/temporary-password-login/temporary-password-login-container'
import { TwoFactorContainer } from './auth/login/two-factor/two-factor-container'
import { ChangePasswordPage } from './auth/password/change/ChangePasswordPage'
import { ForgetPasswordContainer } from './auth/password/forget/forget-password-container'
import { RegisterPasswordContainer } from './auth/password/register/register-password-container'
import { RequiredLogin } from './auth/required-login/reuqired-login'
import { DashboardScreen } from './dashboard/screen'
import { trialIdParamName } from './dashboard/trial/detail/trial-detail'
import { routes } from './routes'
import {
  VideoWithKeyContainer,
  videoKeyParamName,
} from './videos/video-with-key-container'
import { VideoWithQueryContainer } from './videos/video-with-query-container'
import { DeliveryDocUrlIsExpired } from '../features/explanation/components/DeliveryDocUrlIsExpired/DeliveryDocUrlIsExpired'

/** @deprecated import from src/hooks/use-query */
export const useQuery = () => {
  return new URLSearchParams(useLocation().search)
}

const Empty: React.FC = () => {
  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null
    // ログイン直後などにブラウザバックすると空白ページに留まり続けてしまうケースがあるので
    // 3 秒後に /dashboard にリダイレクトさせる
    const f = async () => {
      timeout = setTimeout(() => {
        window.location.assign('/dashboard')
      }, 3000)
    }
    f()
    return () => {
      if (timeout) {
        clearInterval(timeout)
      }
    }
  }, [])

  return <Spinner />
}

export const Router = () => {
  return <RouterProvider router={router} />
}

const withAuthenticator = (
  element: React.ReactElement,
  shouldSelectTrial?: boolean,
) => {
  return (
    <Authenticator shouldSelectTrial={shouldSelectTrial}>
      {element}
    </Authenticator>
  )
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path={`${routes.login}`} element={<LoginContainer />} />
      <Route path={`${routes.callback}`} element={<OAuthCallback />} />
      <Route path={Paths.LogoutComplete} element={<LogoutCompleteScreen />} />
      <Route path={`${routes.twoFactor}`} element={<TwoFactorContainer />} />
      <Route path={Paths.ReleaseNote} element={<ReleaseNoteScreen />} />
      <Route path={Paths.OperationManual} element={<OperationManualScreen />} />
      <Route
        path={`${routes.temporaryPasswordLogin}`}
        element={<TemporaryPasswordLoginContainer />}
      />
      <Route path={`${routes.requiredLogin}`} element={<RequiredLogin />} />
      <Route path={`${routes.httpError403}`} element={<HttpError403 />} />
      <Route
        path={Paths.WorksheetFileViewer}
        element={withAuthenticator(<WorksheetFileViewerScreen />)}
      />
      <Route
        path={Paths.WorksheetPrint}
        element={withAuthenticator(<WorksheetPrintScreen />)}
      />
      <Route
        path={Paths.ReferredPatientFileViewer}
        element={withAuthenticator(<ReferredPatientFileViewerScreen />)}
      />
      <Route
        path={Paths.TrialHospitalSharedFileViewer}
        element={withAuthenticator(<HospitalSharedFileViewer />)}
      />
      <Route
        path={Paths.VideoCallConnectionTest}
        element={withAuthenticator(<VideoCallConnectionTestScreen />, true)}
      />
      <Route path={`${routes.dashboard}/*`} element={<DashboardScreen />} />
      <Route
        path={`${Paths.ExplanationRoom}/*`}
        element={<ExplanationRoomRoutes />}
      />
      <Route
        path={Paths.PatientExpReservationSignIn}
        element={<PatientReservationScreenV2 />}
      />
      <Route
        path={routes.completeDocuSignAction}
        element={<CompleteDocuSignAction />}
      />
      <Route
        path={Paths.HospitalTrialPatientVideoCall}
        element={withAuthenticator(<HospitalTrialPatientVideoCallScreen />)}
      />
      <Route
        path={Paths.PatientTrialPatientVideoCall}
        element={<PatientTrialPatientVideoCallScreen />}
      />
      <Route
        path={`${routes.trials}/:${trialIdParamName}/:${membersParamName}/:${memberIdParamName}/:${AuthenticateTokenParamName}`}
        element={<AuthenticateContainer />}
      />
      <Route
        path={`${routes.forgetPassword}`}
        element={<ForgetPasswordContainer />}
      />
      <Route
        path={`${Paths.RegisterPassword}`}
        element={<RegisterPasswordContainer />}
      />
      <Route
        path={Paths.ChangePassword}
        element={
          <Authenticator shouldSelectTrial={false}>
            <ChangePasswordPage />
          </Authenticator>
        }
      />
      <Route
        path={Paths.PatientVideoCallSignIn}
        element={<PatientVideoCallSignInScreen />}
      />
      <Route
        path={Paths.PatientVideoCallForPatient}
        element={<PatientVideoCallForPatientScreen />}
      />
      <Route
        path={Paths.PatientVideoCall}
        element={withAuthenticator(<PatientVideoCallScreen />)}
      />
      <Route
        path={Paths.PatientVideoCallExpired}
        element={<PatientVideoCallExpiredScreen />}
      />
      <Route path={`${routes.videos}`} element={<VideoWithQueryContainer />} />
      <Route
        path={`${routes.videos}/:${videoKeyParamName}`}
        element={<VideoWithKeyContainer />}
      />
      <Route path={routes.switch} element={<Empty />} />
      <Route path={routes.agreement} element={<AgreementContainer />} />
      <Route path={routes.termsOfService} element={<TermsOfServiceScreen />} />
      <Route
        path={routes.termsOfService2}
        element={<AuthenticatedTermsScreen />}
      />
      <Route path={routes.privacyPolicy} element={<PrivacyPolicyScreen />} />
      <Route
        path={routes.externalTransmissionPolicy}
        element={<ExternalTransmissionPolicyScreen />}
      />
      {/*TODO: remove*/}
      <Route
        path={routes.resendDeliveryMail}
        element={<ResendDeliveryMail />}
      />
      <Route
        path={routes.deliveryDocUrlIsExpired}
        element={<DeliveryDocUrlIsExpired />}
      />
      <Route
        path={Paths.ExplanationSignAuthentication}
        element={<SignAuthScreen />}
      />
      <Route
        path={Paths.PatientExplanationPinSetting}
        element={<PatientPinSettingScreen />}
      />
      <Route path={Paths.DocuSignRedirect} element={null} />
      <Route path="" element={<Navigate to={Paths.Dashboard} />} />
    </>,
  ),
)
